/**
 * ScoreCard — a single transparent score with its value and an expandable "why".
 * Reflects the brief's explainability requirement: no black-box numbers.
 */
import { useState } from 'react';
import type { Score, ScoreCategory } from '@aurora/shared';

const CATEGORY_LABELS: Record<ScoreCategory, string> = {
  scientific_activity: 'Scientific Activity',
  funding_strength: 'Funding Strength',
  collaboration_influence: 'Collaboration Influence',
  technology_alignment: 'Technology Alignment',
  commercial_engagement: 'Commercial Engagement',
};

function band(value: number): string {
  if (value >= 75) return 'strong';
  if (value >= 50) return 'solid';
  if (value >= 25) return 'moderate';
  return 'low';
}

export function ScoreCard({ score }: { score: Score }): JSX.Element {
  const [open, setOpen] = useState(false);
  const label = CATEGORY_LABELS[score.category] ?? score.category;
  const factors = Object.entries(score.factors ?? {});

  return (
    <div className={`score-card band-${band(score.value)}`}>
      <button
        type="button"
        className="score-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="score-label">{label}</span>
        <span className="score-value">{score.value}</span>
        <span className="score-toggle">{open ? '▾' : '▸'}</span>
      </button>
      <div className="score-bar" aria-hidden>
        <div className="score-bar-fill" style={{ width: `${score.value}%` }} />
      </div>
      <p className="score-why">{score.explanation}</p>
      {open && factors.length > 0 && (
        <table className="score-factors">
          <tbody>
            {factors.map(([key, val]) => (
              <tr key={key}>
                <td className="factor-key">{key}</td>
                <td className="factor-val">{String(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
